import {
  AIR_ACCEL,
  AIR_GRAVITY,
  AIR_PASS_HEIGHT,
  BEST_KEY,
  COURSE_WIDTH,
  CRUISE_SPEED,
  FIXED_DT,
  GRAZE_BAND,
  GRAZE_MIN_SPEED,
  GROUND_ACCEL,
  GROUND_DRAG,
  HIT_SPEED_KEEP,
  JUMP_SPEED_BONUS,
  JUMP_VEL,
  KNOCKBACK,
  MAX_SPEED,
  MIN_SPEED,
  PLAYER_RADIUS,
  PLAYER_SCREEN_MAX,
  PLAYER_SCREEN_MIN,
  STEER_BASE,
  STEER_SPEED_GAIN,
  STUN_TIME,
  SUBSTEP_PX,
  TRACK_LENGTH,
  TUCK_ACCEL,
  TUCK_MAX,
} from "./constants";
import { generateCourse, type Entity, type Scenery } from "./course";
import { readMoveX, readTuck, resetTransientInput } from "./input";
import { getObject } from "./objects";
import { mulberry32, xmur3 } from "./rng";
import {
  sfxCollect,
  sfxFinish,
  sfxHit,
  sfxJump,
  sfxLand,
  sfxNearMiss,
  sfxStart,
  tickAmbience,
} from "./audio";
import { musicFinish, musicStart, tickMusic, type MusicState } from "./music";
import { adjustedTimeMs } from "./scoring";

export type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  kind: "snow" | "spray" | "burst";
};

export type Player = {
  x: number;
  y: number;
  speedY: number;
  height: number;
  airVel: number;
  stun: number;
  lean: number;
  squash: number;
  frame: number;
  invuln: number;
};

export type GameSim = {
  seed: number;
  phase: "title" | "playing" | "results";
  time: number;
  clock: number;
  finishTime: number;
  // Finish clock less the food bonus, in ms -- what the board ranks and what
  // the personal best is kept in. 0 until the run finishes.
  adjustedMs: number;
  best: number;
  isBest: boolean;
  hotDogs: number;
  burgers: number;
  ramps: number;
  crashes: number;
  grazes: number;
  player: Player;
  entities: Entity[];
  scenery: Scenery[];
  particles: Particle[];
  tracks: { x: number; y: number }[];
  camX: number;
  camY: number;
  camRoll: number;
  trauma: number;
  hitstop: number;
  width: number;
  length: number;
  reducedMotion: boolean;
};

export type HudSnapshot = {
  time: number;
  speed: number;
  air: boolean;
  stun: boolean;
  // Whether a tuck input is held right now -- the HUD teaches the control, so
  // it needs the input-level truth, not the physics gate.
  tuck: boolean;
  progress: number;
  phase: GameSim["phase"];
  best: number;
  isBest: boolean;
  hotDogs: number;
  burgers: number;
  adjustedMs: number;
  // The hill this run was set on. Stored with the submission, unranked, so a
  // top score can be re-ridden.
  seed: number;
};

function loadBest(): number {
  try {
    if (typeof localStorage === "undefined") return 0;
    const raw = localStorage.getItem(BEST_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function saveBest(ms: number): void {
  try {
    localStorage.setItem(BEST_KEY, String(ms));
  } catch {}
}

function prefersReducedMotion(): boolean {
  try {
    return (
      typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
}

function makePlayer(): Player {
  return {
    x: COURSE_WIDTH / 2,
    y: 40,
    speedY: CRUISE_SPEED * 0.55,
    height: 0,
    airVel: 0,
    stun: 0,
    lean: 0,
    squash: 1,
    frame: 0,
    invuln: 0,
  };
}

export function createSim(seed = (Math.random() * 0xffffffff) >>> 0): GameSim {
  const rng = mulberry32(seed);
  const sceneryRng = mulberry32(seed ^ 0x9e3779b9);
  const pickupRng = mulberry32(seed ^ 0x85ebca6b);
  const course = generateCourse(rng, sceneryRng, pickupRng);
  return {
    seed,
    phase: "title",
    time: 0,
    clock: 0,
    finishTime: 0,
    adjustedMs: 0,
    best: loadBest(),
    isBest: false,
    hotDogs: 0,
    burgers: 0,
    ramps: 0,
    crashes: 0,
    grazes: 0,
    player: makePlayer(),
    entities: course.entities,
    scenery: course.scenery,
    particles: [],
    tracks: [],
    camX: COURSE_WIDTH / 2,
    camY: 0,
    camRoll: 0,
    trauma: 0,
    hitstop: 0,
    width: course.width,
    length: course.length,
    reducedMotion: prefersReducedMotion(),
  };
}

export function resetRun(sim: GameSim, seed?: number): void {
  const next = createSim(seed ?? (Math.random() * 0xffffffff) >>> 0);
  Object.assign(sim, next);
}

export function startRace(sim: GameSim): void {
  if (sim.phase === "playing") return;
  if (sim.phase === "results") resetRun(sim);
  resetTransientInput();
  sim.phase = "playing";
  sim.time = 0;
  sim.finishTime = 0;
  sim.adjustedMs = 0;
  sim.isBest = false;
  sim.hotDogs = 0;
  sim.burgers = 0;
  sim.ramps = 0;
  sim.crashes = 0;
  sim.grazes = 0;
  sim.reducedMotion = prefersReducedMotion();
  sim.player.speedY = CRUISE_SPEED * 0.72;
  sim.player.y = 24;
  sim.player.x = COURSE_WIDTH / 2;
  sim.player.stun = 0;
  sim.player.invuln = 0;
  sim.player.height = 0;
  sim.player.airVel = 0;
  sfxStart();
  musicStart(sim.seed);
}

export function retryRace(sim: GameSim): void {
  resetRun(sim);
  startRace(sim);
}

function emit(
  sim: GameSim,
  kind: Particle["kind"],
  x: number,
  y: number,
  n: number,
  speed: number,
) {
  for (let i = 0; i < n; i++) {
    if (sim.particles.length > 220) sim.particles.shift();
    const a = Math.random() * Math.PI * 2;
    const s = speed * (0.4 + Math.random());
    const life = 0.25 + Math.random() * 0.45;
    sim.particles.push({
      x,
      y,
      vx: Math.cos(a) * s,
      vy: Math.sin(a) * s * 0.6 + (kind === "spray" ? -40 : 80),
      life,
      max: life,
      size: kind === "burst" ? 3 + Math.random() * 4 : 1.5 + Math.random() * 2.5,
      kind,
    });
  }
}

function aabbOverlap(
  ax: number,
  ay: number,
  aw: number,
  ah: number,
  bx: number,
  by: number,
  bw: number,
  bh: number,
): boolean {
  return Math.abs(ax - bx) * 2 < aw + bw && Math.abs(ay - by) * 2 < ah + bh;
}

function circleOverlap(
  ax: number,
  ay: number,
  ar: number,
  bx: number,
  by: number,
  br: number,
): boolean {
  const dx = ax - bx;
  const dy = ay - by;
  const r = ar + br;
  return dx * dx + dy * dy < r * r;
}

function solidRadius(hitbox: { w: number; h: number }): number {
  return Math.max(hitbox.w, hitbox.h) * 0.42;
}

function applySolidHit(sim: GameSim, entity: Entity, radius: number): void {
  const p = sim.player;
  if (p.stun > 0 || p.invuln > 0) return;
  p.stun = STUN_TIME;
  p.invuln = STUN_TIME + 0.12;
  p.y = Math.max(0, p.y - KNOCKBACK);
  const minSep = PLAYER_RADIUS + radius + 4;
  if (p.y > entity.y - minSep) p.y = Math.max(0, entity.y - minSep);
  p.speedY = Math.max(MIN_SPEED, p.speedY * HIT_SPEED_KEEP);
  p.height = 0;
  p.airVel = 0;
  p.squash = 1.25;
  entity.ignoreUntil = 1;
  sim.crashes += 1;
  sim.trauma = Math.min(1, sim.trauma + 0.55);
  sim.hitstop = 0.055;
  emit(sim, "burst", p.x, p.y, 18, 160);
  sfxHit(entity.defId === "rock" ? "rock" : "tree", p.x);
}

function applyJump(sim: GameSim, entity: Entity): void {
  const p = sim.player;
  if (p.height > 4 || entity.consumed || p.stun > 0) return;
  entity.consumed = true;
  p.airVel = JUMP_VEL + p.speedY * JUMP_SPEED_BONUS;
  p.height = 6;
  p.squash = 0.82;
  sim.ramps += 1;
  emit(sim, "spray", p.x, p.y, 14, 90);
  sfxJump(p.x);
}

// Pickups run as their own pass, ahead of the obstacle scan, for two reasons
// the obstacle loop cannot accommodate:
//
//   - it skips everything while airborne, and a hot dog you sailed over should
//     still be a hot dog you ate;
//   - it returns the moment it lands a solid hit, so a pickup sitting later in
//     the entity list than the tree you just clipped would be silently skipped
//     in that substep.
function collectPickups(sim: GameSim): void {
  const p = sim.player;
  for (const e of sim.entities) {
    if (e.consumed) continue;
    if (Math.abs(e.y - p.y) > 140 || Math.abs(e.x - p.x) > 140) continue;
    const def = getObject(e.defId);
    if (def.collision !== "pickup") continue;
    const box = PLAYER_RADIUS * 2;
    if (!aabbOverlap(p.x, p.y, box, box, e.x, e.y, def.hitbox.w, def.hitbox.h)) continue;
    e.consumed = true;
    if (e.defId === "burger") sim.burgers += 1;
    else sim.hotDogs += 1;
    emit(sim, "spray", e.x, e.y, 12, 95);
    sfxCollect(e.defId === "burger" ? "burger" : "hotdog", e.x);
  }
}

function collide(sim: GameSim): void {
  const p = sim.player;
  const airborne = p.height > AIR_PASS_HEIGHT;
  const range = 140;
  for (const e of sim.entities) {
    if (Math.abs(e.y - p.y) > range || Math.abs(e.x - p.x) > range) continue;
    const def = getObject(e.defId);
    if (def.collision === "none" || def.collision === "pickup") continue;
    if (airborne) continue;
    if (def.collision === "solid") {
      const r = solidRadius(def.hitbox);
      if (e.ignoreUntil > 0) {
        if (p.y > e.y + r + PLAYER_RADIUS + 8) e.ignoreUntil = 0;
        else continue;
      }
      if (circleOverlap(p.x, p.y, PLAYER_RADIUS, e.x, e.y, r)) {
        applySolidHit(sim, e, r);
        return;
      }
    } else if (def.collision === "launch") {
      if (
        aabbOverlap(
          p.x,
          p.y,
          PLAYER_RADIUS * 2,
          PLAYER_RADIUS * 2,
          e.x,
          e.y,
          def.hitbox.w,
          def.hitbox.h,
        )
      ) {
        applyJump(sim, e);
      }
    }
  }
}

// A near-miss is a solid drawn level with the rider this substep -- prevY on
// one side of it, y on the other, so each obstacle can fire only once --
// inside a band that starts just past the crash radius. Skipped while
// stunned: a whoosh straight after a crash is noise, not a thrill.
function detectGrazes(sim: GameSim, prevY: number): void {
  const p = sim.player;
  if (p.stun > 0 || p.height > AIR_PASS_HEIGHT || p.speedY < GRAZE_MIN_SPEED) return;
  for (const e of sim.entities) {
    if (e.ignoreUntil > 0) continue;
    if (e.y <= prevY || e.y > p.y) continue;
    const def = getObject(e.defId);
    if (def.collision !== "solid") continue;
    const clear = PLAYER_RADIUS + solidRadius(def.hitbox);
    const lateral = Math.abs(e.x - p.x);
    if (lateral > clear + 2 && lateral <= clear + GRAZE_BAND) {
      sim.grazes += 1;
      sfxNearMiss(p.speedY, e.x);
    }
  }
}

function stepPhysics(sim: GameSim, dt: number): void {
  const p = sim.player;
  p.stun = Math.max(0, p.stun - dt);
  p.invuln = Math.max(0, p.invuln - dt);

  const moveX = p.stun > 0 ? 0 : readMoveX();
  const steer = STEER_BASE + p.speedY * STEER_SPEED_GAIN;
  p.x += moveX * steer * dt;
  p.x = Math.max(PLAYER_RADIUS + 8, Math.min(COURSE_WIDTH - PLAYER_RADIUS - 8, p.x));
  p.lean += (moveX - p.lean) * (1 - Math.exp(-14 * dt));

  const wasAir = p.height > 0.5;
  if (p.height > 0 || p.airVel > 0) {
    p.airVel -= AIR_GRAVITY * dt;
    p.height += p.airVel * dt;
    if (p.height <= 0) {
      // Touchdown speed, read before the reset: it decides how hard the
      // landing sounds.
      const fall = -p.airVel;
      p.height = 0;
      p.airVel = 0;
      if (wasAir) {
        p.squash = 1.22;
        emit(sim, "spray", p.x, p.y, 10, 70);
        sfxLand(fall, p.x);
      }
    }
  }

  // Tucking is a ground posture: the air already has its own acceleration,
  // and a stun cancels it with everything else.
  const tucking = p.stun <= 0 && p.height <= 0.5 && readTuck();
  if (p.height > AIR_PASS_HEIGHT) {
    p.speedY += AIR_ACCEL * dt;
  } else if (p.stun <= 0) {
    if (tucking && p.speedY < TUCK_MAX) {
      p.speedY = Math.min(TUCK_MAX, p.speedY + TUCK_ACCEL * dt);
    } else if (p.speedY < CRUISE_SPEED) {
      p.speedY += GROUND_ACCEL * dt;
    } else if (!tucking || p.speedY > TUCK_MAX) {
      // Held at the cap: a tuck keeps a ramp landing's speed at TUCK_MAX
      // instead of letting drag bleed it back to cruise.
      p.speedY -= GROUND_DRAG * dt;
    }
  }

  p.speedY = Math.max(MIN_SPEED, Math.min(MAX_SPEED, p.speedY));
  const prevY = p.y;
  p.y += p.speedY * dt;

  collectPickups(sim);
  collide(sim);
  detectGrazes(sim, prevY);

  // The resting shape leans into the crouch while tucked; the same relaxation
  // smooths the transition both ways.
  const rest = tucking ? 1.06 : 1;
  p.squash += (rest - p.squash) * (1 - Math.exp(-10 * dt));
  p.frame += dt * (6 + p.speedY * 0.01);

  if (sim.phase === "playing" && p.height < 4) {
    const last = sim.tracks[sim.tracks.length - 1];
    if (!last || Math.hypot(last.x - p.x, last.y - p.y) > 10) {
      sim.tracks.push({ x: p.x, y: p.y });
      if (sim.tracks.length > 80) sim.tracks.shift();
    }
  }

  if (sim.phase === "playing" && p.y >= TRACK_LENGTH && !sim.finishTime) {
    sim.finishTime = sim.time;
    sim.phase = "results";
    sim.adjustedMs = adjustedTimeMs({
      finishSeconds: sim.time,
      hotDogs: sim.hotDogs,
      burgers: sim.burgers,
    });
    // Best is kept in the board's own currency, adjusted ms, so the title
    // screen and the leaderboard can never disagree about which run was
    // better: a slower clock with more food can be the new best.
    sim.isBest = sim.best <= 0 || sim.adjustedMs < sim.best;
    if (sim.isBest) {
      sim.best = sim.adjustedMs;
      saveBest(sim.adjustedMs);
    }
    resetTransientInput();
    sfxFinish(sim.isBest);
    musicFinish(sim.isBest);
    emit(sim, "burst", p.x, p.y, 28, 120);
  }
}

function stepParticles(sim: GameSim, dt: number): void {
  let n = 0;
  const list = sim.particles;
  for (let i = 0; i < list.length; i++) {
    const q = list[i]!;
    q.life -= dt;
    q.x += q.vx * dt;
    q.y += q.vy * dt;
    q.vy += 80 * dt;
    if (q.life > 0) list[n++] = q;
  }
  list.length = n;

  if (sim.player.height < 3 && sim.player.speedY > 240 && Math.random() < dt * 18) {
    emit(sim, "spray", sim.player.x + (Math.random() - 0.5) * 16, sim.player.y - 8, 1, 40);
  }
}

function speedFraction(speedY: number): number {
  return Math.max(0, Math.min(1, (speedY - MIN_SPEED) / (MAX_SPEED - MIN_SPEED)));
}

function musicStateFor(sim: GameSim): MusicState {
  const playing = sim.phase === "playing";
  return {
    phase: sim.phase,
    progress: Math.max(0, Math.min(1, sim.player.y / TRACK_LENGTH)),
    speed01: playing ? speedFraction(sim.player.speedY) : 0,
    crashes: sim.crashes,
  };
}

export function stepSim(sim: GameSim, dt: number): void {
  sim.clock += dt;
  // Pumped ahead of every early return below. The score's scheduler has to
  // keep running through a hitstop and behind the results card, and a gap in
  // it is an audible hole in the music rather than a dropped frame.
  tickMusic(musicStateFor(sim));
  if (sim.phase !== "playing") {
    stepParticles(sim, dt);
    tickAmbience({ speed01: 0.15, grounded: false, lean: 0, stunned: false, idle: true });
    return;
  }
  if (sim.hitstop > 0) {
    sim.hitstop -= dt;
    return;
  }
  sim.time += dt;

  const travel = sim.player.speedY * dt;
  const sub = Math.max(1, Math.ceil(travel / SUBSTEP_PX));
  const sdt = dt / sub;
  for (let i = 0; i < sub; i++) stepPhysics(sim, sdt);

  stepParticles(sim, dt);
  sim.trauma = Math.max(0, sim.trauma - dt * 1.8);
  const targetRoll = sim.reducedMotion ? 0 : sim.player.lean * 0.05;
  sim.camRoll += (targetRoll - sim.camRoll) * (1 - Math.exp(-8 * dt));
  tickAmbience({
    speed01: speedFraction(sim.player.speedY),
    grounded: sim.player.height <= 0.5,
    lean: sim.player.lean,
    stunned: sim.player.stun > 0,
    idle: false,
  });
}

export function playerScreenFrac(sim: GameSim): number {
  const t = Math.max(
    0,
    Math.min(1, (sim.player.speedY - CRUISE_SPEED) / (MAX_SPEED - CRUISE_SPEED)),
  );
  return PLAYER_SCREEN_MAX + (PLAYER_SCREEN_MIN - PLAYER_SCREEN_MAX) * t;
}

export function hudSnapshot(sim: GameSim): HudSnapshot {
  return {
    time: sim.phase === "results" ? sim.finishTime : sim.time,
    speed: sim.player.speedY,
    air: sim.player.height > AIR_PASS_HEIGHT,
    stun: sim.player.stun > 0,
    tuck: readTuck(),
    progress: Math.max(0, Math.min(1, sim.player.y / TRACK_LENGTH)),
    phase: sim.phase,
    best: sim.best,
    isBest: sim.isBest,
    hotDogs: sim.hotDogs,
    burgers: sim.burgers,
    adjustedMs: sim.adjustedMs,
    seed: sim.seed,
  };
}

export function hashSeed(text: string): number {
  return xmur3(text);
}

export { FIXED_DT };
